import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Search, FileText, CheckCircle2 } from "lucide-react";

export default function COA() {
  const [coas, setCoas] = useState([]);
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || params.get("batch") || "";
  const [query, setQuery] = useState(initial);

  const fetchCoas = (q = "") => {
    api.get("/coas", { params: q ? { q } : {} }).then((r) => setCoas(r.data));
  };

  useEffect(() => { fetchCoas(initial); }, [initial]);

  const submit = (e) => {
    e.preventDefault();
    setParams(query ? { q: query } : {});
    fetchCoas(query);
  };

  return (
    <div data-testid="coa-page" className="bg-sand">
      <section className="bg-white border-b border-tan/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <span className="eyebrow">Lab Transparency</span>
          <h1 className="font-serif text-5xl sm:text-6xl text-navy mt-4 leading-tight">Certificates of <span className="italic text-copper">Analysis.</span></h1>
          <div className="divider my-6" />
          <p className="text-lg text-charcoal/85 max-w-2xl font-light leading-relaxed">
            Every batch we sell is tested by a third-party laboratory for cannabinoid potency and contaminants.
            Search by product name or batch number, or browse the most recent results.
          </p>
          <form onSubmit={submit} className="mt-10 flex gap-2 max-w-xl" data-testid="coa-search-form">
            <input
              data-testid="coa-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by product name or batch number"
              className="flex-1 border border-tan/50 px-4 py-3 bg-white focus:border-copper focus:outline-none"
            />
            <button data-testid="coa-search-btn" type="submit" className="bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-charcoal flex items-center gap-2">
              <Search size={16}/> Search
            </button>
          </form>
        </div>
      </section>

      <section className="bg-sand/50 py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          {coas.length === 0 ? (
            <div className="text-center text-charcoal/70 py-16">No matching certificates found.</div>
          ) : (
            <div className="space-y-4">
              {coas.map((c) => (
                <div key={c.id} data-testid={`coa-row-${c.batch_number}`} className="bg-white border border-tan/40 p-7 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-copper" />
                      <span className="font-serif text-xl text-navy">{c.product_name}</span>
                    </div>
                    <div className="mt-2 text-sm text-charcoal/80">
                      Batch <span className="font-medium text-navy">{c.batch_number}</span> · Tested {c.test_date} · {c.lab_name}
                    </div>
                    <p className="mt-2 text-sm text-charcoal/75 max-w-xl">{c.summary}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center gap-1 text-sm text-[#2f6f3a]">
                      <CheckCircle2 size={16}/> {c.pass_fail}
                    </span>
                    {c.pdf_url ? (
                      <a href={mediaUrl(c.pdf_url)} target="_blank" rel="noreferrer" className="bg-navy text-white px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-charcoal">View PDF</a>
                    ) : (
                      <span className="text-xs text-charcoal/60 italic">PDF available on request</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
